/**
 * TaskCalendarPopover - Phase 6
 * 
 * Popover component that appears when a date is selected in CalendarView.
 * Shows tasks for that day with quick actions.
 * 
 * @module TaskCalendarPopover
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Plus, 
  CheckCircle2, 
  Circle, 
  Clock,
  AlertCircle,
  DollarSign
} from 'lucide-react';

// ============================================================================
// STYLES
// ============================================================================

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.3)',
    zIndex: 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  popover: {
    background: 'white',
    borderRadius: '16px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    width: '100%',
    maxWidth: '400px',
    maxHeight: '80vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid #f3f4f6',
  },
  dateTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1f2937',
  },
  closeButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    border: 'none',
    background: '#f3f4f6',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6b7280',
    transition: 'all 0.2s',
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '16px',
  },
  taskList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  taskItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '12px',
    background: '#f9fafb',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  taskCheckbox: {
    marginTop: '2px',
    cursor: 'pointer',
    flexShrink: 0,
  },
  taskContent: {
    flex: 1,
    minWidth: 0,
  },
  taskTitle: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#1f2937',
    marginBottom: '2px',
  },
  taskMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: '#6b7280',
  },
  priorityBadge: {
    fontSize: '10px',
    fontWeight: '600',
    padding: '2px 6px',
    borderRadius: '4px',
    textTransform: 'uppercase',
  },
  financialBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    fontSize: '12px',
    fontWeight: '600',
    color: '#059669',
  },
  emptyState: {
    padding: '40px 20px',
    textAlign: 'center',
  },
  emptyIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    background: '#f3f4f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },
  emptyTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: '4px',
  },
  emptyDesc: {
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '16px',
  },
  footer: {
    padding: '12px 16px',
    borderTop: '1px solid #f3f4f6',
  },
  addButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px',
    background: '#006D77',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
};

// Priority styling
const PRIORITY_STYLES = {
  high: { background: '#fef2f2', color: '#dc2626' },
  medium: { background: '#fff7ed', color: '#ea580c' },
  low: { background: '#eff6ff', color: '#2563eb' },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function formatDateTitle(date) {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const isToday = date.toDateString() === today.toDateString();
  const isTomorrow = date.toDateString() === tomorrow.toDateString();
  
  if (isToday) return 'Today';
  if (isTomorrow) return 'Tomorrow';
  
  return date.toLocaleDateString('en-US', { 
    weekday: 'long',
    month: 'short', 
    day: 'numeric' 
  });
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const TaskCalendarPopover = ({
  isOpen,
  onClose,
  date,
  tasks = [],
  onToggleTask,
  onAddTask,
  onTaskClick,
  currencySymbol = '£',
}) => {
  if (!isOpen || !date) return null;

  const handleToggle = (e, task) => {
    e.stopPropagation();
    onToggleTask?.(task);
  };

  const handleAddClick = () => {
    onAddTask?.(date);
    onClose();
  };

  const handleTaskClick = (task) => {
    onTaskClick?.(task);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={styles.overlay}
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            style={styles.popover}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={styles.header}>
              <div style={styles.dateTitle}>
                {formatDateTitle(date)}
              </div>
              <button
                style={styles.closeButton}
                onClick={onClose}
                onMouseOver={(e) => e.target.style.background = '#e5e7eb'}
                onMouseOut={(e) => e.target.style.background = '#f3f4f6'}
              >
                <X size={16} />
              </button>
            </div>

            {/* Content */}
            <div style={styles.content}>
              {tasks.length === 0 ? (
                <div style={styles.emptyState}>
                  <div style={styles.emptyIcon}>
                    <Clock size={28} color="#9ca3af" />
                  </div>
                  <div style={styles.emptyTitle}>No tasks scheduled</div>
                  <div style={styles.emptyDesc}>
                    Add a task to keep track of what needs to be done
                  </div>
                </div>
              ) : (
                <div style={styles.taskList}>
                  {tasks.map((task) => {
                    const isDone = task.status === 'done';
                    const priorityStyle = PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.medium;

                    return (
                      <motion.div
                        key={task.id}
                        style={{
                          ...styles.taskItem,
                          background: isDone ? '#f0fdf4' : '#f9fafb',
                        }}
                        whileHover={{ background: isDone ? '#dcfce7' : '#f3f4f6' }}
                        onClick={() => handleTaskClick(task)}
                      >
                        {/* Checkbox */}
                        <div 
                          style={styles.taskCheckbox}
                          onClick={(e) => handleToggle(e, task)}
                        >
                          {isDone ? (
                            <CheckCircle2 size={20} color="#10b981" />
                          ) : (
                            <Circle size={20} color="#9ca3af" />
                          )}
                        </div>

                        {/* Content */}
                        <div style={styles.taskContent}>
                          <div style={{
                            ...styles.taskTitle,
                            textDecoration: isDone ? 'line-through' : 'none',
                            color: isDone ? '#9ca3af' : '#1f2937',
                          }}>
                            {task.title}
                          </div>
                          <div style={styles.taskMeta}>
                            {/* Priority Badge */}
                            <span style={{
                              ...styles.priorityBadge,
                              ...priorityStyle,
                            }}>
                              {task.priority}
                            </span>

                            {/* Financial Amount */}
                            {task.isFinancialTask && task.estimatedAmount > 0 && (
                              <span style={styles.financialBadge}>
                                <DollarSign size={12} />
                                {currencySymbol}{task.estimatedAmount.toFixed(2)}
                              </span>
                            )}

                            {/* Time if available */}
                            {task.dueDate && (
                              <span>
                                {(() => {
                                  const d = task.dueDate?.toDate ? task.dueDate.toDate() : new Date(task.dueDate);
                                  const hours = d.getHours();
                                  const mins = d.getMinutes();
                                  if (hours === 0 && mins === 0) return null;
                                  return d.toLocaleTimeString('en-US', { 
                                    hour: 'numeric', 
                                    minute: '2-digit',
                                    hour12: true 
                                  });
                                })()}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Overdue indicator */}
                        {!isDone && task.dueDate && (() => {
                          const d = task.dueDate?.toDate ? task.dueDate.toDate() : new Date(task.dueDate);
                          return d < new Date();
                        })() && (
                          <AlertCircle size={16} color="#ef4444" />
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={styles.footer}>
              <motion.button
                style={styles.addButton}
                whileHover={{ background: '#005a63' }}
                whileTap={{ scale: 0.98 }}
                onClick={handleAddClick}
              >
                <Plus size={18} />
                Add Task for {formatDateTitle(date)}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TaskCalendarPopover;
